import {
  GeneoIotError,
  GeneoIotFrame,
  GeneoIotProjectFile,
  GeneoIotProjectMeta,
  IStyles,
  pxToRem,
  pxTovW,
  SecondaryButton,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { setTeacherSolution } from '../CreateProject/reducer/projectFlow.Slice';

/** Floor for the editor height so it stays usable on short viewports. */
const EDITOR_MIN_HEIGHT = 420;

const styles: IStyles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(12), md: pxToRem(18), lg: pxTovW(20) },
  },
  editorWrapper: {
    width: '100%',
    display: 'flex',
    borderRadius: { xs: pxToRem(12), md: pxToRem(18), lg: pxTovW(20) },
    overflow: 'hidden',
    border: '1px solid #CCE6FF',
    backgroundColor: 'common.white',
    // The editor needs real vertical room; the subtraction leaves space for the
    // page heading and the stepper sitting above it.
    height: {
      xs: `max(${EDITOR_MIN_HEIGHT}px, calc(100vh - ${pxToRem(260)}))`,
      md: `max(${EDITOR_MIN_HEIGHT}px, calc(100vh - ${pxTovW(360)}))`,
    },
  },
  notice: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(12), md: pxToRem(16), lg: pxTovW(20) },
    padding: { xs: pxToRem(16), md: pxToRem(18), lg: pxTovW(24) },
    borderRadius: { xs: pxToRem(12), md: pxToRem(18), lg: pxTovW(20) },
    border: '1px solid #F8C9A4',
    backgroundColor: '#FFF6EE',
  },
  frame: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    display: 'block',
  },
};

interface IProjectSettingsProps {
  /**
   * The solution is stored on the selection's practical resource, so without
   * one there is nowhere to save it - the editor is replaced by a notice.
   */
  hasPracticalResource: boolean;
}

export const ProjectSettings = ({
  hasPracticalResource,
}: IProjectSettingsProps) => {
  const dispatch = useAppDispatch();
  const { user_info } = useAppSelector((state) => state.auth);
  const { project_name, selected_subject, teacher_solution } = useAppSelector(
    (state) => state.projectFlow
  );

  /**
   * Names the reference project the teacher is authoring. The frame uses it for
   * the blank project it opens with, so the teacher is not looking at
   * "geneo_iot_untitled".
   */
  const meta: GeneoIotProjectMeta = {
    name: project_name || 'Untitled Project',
    author: [user_info?.firstName, user_info?.lastName]
      .filter(Boolean)
      .join(' '),
    description: selected_subject?.name ?? '',
  };

  // The frame pushes its project whenever the teacher saves inside the editor.
  // The project does not exist yet at this point, so there is nothing to save
  // it against: it is held in the flow state and goes out with CREATE PROJECT,
  // as the practical resource's `metaData.practical.teacherSolution`. The toast
  // is what tells the teacher the save landed, since no call leaves the page.
  const handleProject = (project: GeneoIotProjectFile) => {
    dispatch(setTeacherSolution(project));
    dispatch(
      setToastInfo({
        label: 'Solution saved. It is stored when you create the project.',
        variant: 'success',
        open: true,
      })
    );
  };

  const handleError = (error: GeneoIotError) => {
    console.error('Project settings - Geneo IoT error', error);
  };

  if (!hasPracticalResource) {
    return (
      <Box sx={styles.root}>
        <Typography variant="h2" fontWeight="bold">
          Project Settings
        </Typography>
        <Typography variant="bodyText" color="text.secondary">
          The resources you selected don't need a reference solution, so there
          is nothing to set up here. Click CREATE PROJECT to continue.
        </Typography>
        {/* Saved earlier, before the practical was deselected - it cannot be
            stored now, so let the teacher drop it instead of being blocked. */}
        {teacher_solution && (
          <Box sx={styles.notice}>
            <Typography variant="bodyText">
              You saved a reference solution earlier, but the resources now
              selected can't hold one. Discard it to create the project.
            </Typography>
            <SecondaryButton
              onClick={() => dispatch(setTeacherSolution(undefined))}
            >
              Discard solution
            </SecondaryButton>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <Typography variant="h2" fontWeight="bold">
        Project Settings
      </Typography>
      <Typography variant="bodyText" color="text.secondary">
        Build the reference solution for this project. Use Save to Geneo inside
        the editor to save your work.
      </Typography>

      <Box sx={styles.editorWrapper}>
        <GeneoIotFrame
          // Stepping back to resource selection unmounts the frame, so a
          // solution already saved is pushed back in on the way forward.
          initialProject={teacher_solution}
          meta={meta}
          readOnly={false}
          onProject={handleProject}
          onError={handleError}
          sx={styles.frame}
          title="Project code editor"
        />
      </Box>
    </Box>
  );
};
